import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';

const AcceptableUse: React.FC = () => {
  const theme = useTheme();
  const lastUpdated = 'February 17, 2026';

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Acceptable Use Policy
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: `1px solid ${theme.palette.divider}` }}>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Acceptable Use Policy ("Policy") governs your use of the Tier One Content platform (the "Service")
            provided by Jakatech LLC, a Texas limited liability company doing business as Tier One Content
            ("Jakatech," "we," "us," or "our"). This Policy is incorporated into and forms part of our{' '}
            <MuiLink component={Link} to="/legal/terms">
              Terms of Service
            </MuiLink>
            .
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By accessing or using the Service, you agree to comply with this Policy. Violations may result in
            suspension or termination of your account, removal of content, and legal action where appropriate.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            1. Purpose and Scope
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Policy describes prohibited uses of the Service. It applies to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                All users (individual, team, and organization accounts)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                All content uploaded, shared, or transmitted through the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                All interactions with our systems, APIs, and infrastructure
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                All communications with other users or our support team
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We reserve the right to monitor use of the Service to ensure compliance with this Policy.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            2. Prohibited Content
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may not upload, share, distribute, or otherwise make available through the Service any content that:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2.1 Illegal or Harmful Content
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Violates any applicable law, regulation, or court order
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Promotes or facilitates illegal activities
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains malware, viruses, ransomware, spyware, or other harmful code
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Exploits or harms minors in any way
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Depicts or promotes violence, self-harm, or dangerous activities
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2.2 Infringing Content
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Infringes intellectual property rights (copyrights, trademarks, patents, trade secrets)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains pirated or illegally obtained material
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Violates publicity or privacy rights
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Misappropriates proprietary information
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2.3 Abusive or Harmful Content
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Constitutes harassment, bullying, or stalking
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains hate speech or promotes discrimination based on race, ethnicity, religion, gender, sexual orientation, disability, or other protected characteristics
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Threatens, intimidates, or incites violence
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains defamatory, libelous, or fraudulent statements
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Discloses private information without consent (doxxing)
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2.4 Deceptive Content
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains spam, phishing attempts, or scams
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Misleads or deceives users
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Impersonates individuals, organizations, or entities
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains false or misleading information presented as fact
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2.5 Adult Content
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains sexually explicit material (unless expressly permitted for legitimate business purposes and properly restricted)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Contains obscene or pornographic content
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            3. Prohibited Conduct
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may not:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3.1 Security and Access Violations
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Attempt to gain unauthorized access to the Service, accounts, systems, or networks
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Circumvent or disable security features, authentication mechanisms, or access controls
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Probe, scan, or test vulnerabilities without prior written authorization
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Share account credentials or allow unauthorized account access
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access or use another user's account without permission
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3.2 Service Disruption
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Overload, flood, or crash the Service or its infrastructure
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Launch denial-of-service (DoS) or distributed denial-of-service (DDoS) attacks
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Interfere with or disrupt other users' access to the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Use automated scripts, bots, or crawlers to access the Service in ways that exceed reasonable human use
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Consume excessive bandwidth or storage resources
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3.3 Misuse of the Service
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Use the Service for any purpose other than content storage, organization, and sharing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Resell, sublicense, or redistribute the Service without authorization
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Reverse engineer, decompile, or disassemble the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Remove, obscure, or alter any legal notices, proprietary markings, or branding
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Use the Service to store or back up data in violation of data residency or sovereignty laws
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3.4 Privacy and Confidentiality Violations
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access, collect, or store personal data of others without consent
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Violate others' privacy rights or confidentiality obligations
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Intercept, monitor, or record communications without authorization
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Share confidential information without proper authorization
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3.5 Impersonation and Misrepresentation
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Impersonate any person, organization, or entity
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Falsely claim affiliation with any person, organization, or entity
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Create accounts using false or misleading information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Misrepresent the source, origin, or ownership of content
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3.6 Abusive Behavior
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Harass, threaten, or intimidate other users or our personnel
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Engage in discriminatory behavior
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Make unsolicited commercial communications (spam)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Manipulate or game our systems (e.g., fake engagement, review bombing)
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            4. API and Developer Conduct
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you use our APIs or develop applications that interact with the Service, you must:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Comply with API rate limits and usage restrictions
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Use authentication tokens and credentials securely
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Implement proper error handling and respect API responses
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Not use the API to circumvent feature limitations or subscription tiers
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Not create applications that violate this Policy or applicable laws
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Promptly respond to our requests to address security or compliance issues
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            5. Compliance with Laws and Regulations
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You must comply with all applicable laws and regulations when using the Service, including:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Data protection and privacy laws (GDPR, CCPA, etc.)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Export control and sanctions regulations
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Anti-spam and electronic communications laws (CAN-SPAM, TCPA, etc.)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Accessibility requirements where applicable
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Industry-specific regulations (HIPAA, FERPA, financial regulations, etc.)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Computer fraud and abuse laws
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>Important:</strong> The Service is not designed for regulated industries requiring specific compliance certifications (e.g., HIPAA, PCI-DSS, FedRAMP). You are responsible for ensuring your use complies with applicable regulations.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            6. Monitoring and Enforcement
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            6.1 Our Rights
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We reserve the right (but have no obligation) to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Monitor use of the Service for compliance with this Policy
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Investigate suspected violations
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Review content or activity when we have a good-faith belief that a violation has occurred
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Respond to legal process or law enforcement requests
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Use automated systems to detect prohibited content or conduct
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not pre-screen all content but will respond to reports of violations.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            6.2 Enforcement Actions
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you violate this Policy, we may take any or all of the following actions:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Warning:</strong> Issue a formal warning
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Content Removal:</strong> Remove or disable access to prohibited content
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Feature Restriction:</strong> Restrict access to certain features
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Temporary Suspension:</strong> Temporarily suspend your account
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Permanent Termination:</strong> Permanently terminate your account and delete your data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Legal Action:</strong> Pursue civil or criminal legal remedies
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Reporting:</strong> Report violations to law enforcement, regulatory authorities, or affected third parties
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Enforcement actions are at our sole discretion and may be taken without prior notice, especially for severe violations.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            6.3 Repeat Offenders
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We maintain records of policy violations. Repeated violations will result in escalating enforcement actions, up to and including permanent account termination.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            7. Reporting Violations
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            7.1 How to Report
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you believe someone is violating this Policy, please report it to us:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Email:</strong> abuse@tieronecontent.com
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Support:</strong> Via the in-app support system
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Copyright Claims:</strong> dmca@tieronecontent.com (see our{' '}
                <MuiLink component={Link} to="/legal/terms">
                  DMCA Policy
                </MuiLink>
                )
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            7.2 What to Include
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            When reporting a violation, please provide:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Your contact information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Description of the violation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Links or references to the prohibited content or conduct
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Any supporting evidence or documentation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Specific sections of this Policy that were violated
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We will investigate all good-faith reports. False or malicious reports may themselves constitute a violation.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            7.3 Response Timeline
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We aim to respond to reports within 2-5 business days. Urgent matters (e.g., illegal content, imminent harm) will be prioritized. The specific actions we take will not necessarily be disclosed to the reporter due to privacy considerations.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            8. Consequences of Violations
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Violations of this Policy may result in:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Immediate loss of access to the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Forfeiture of any fees paid (no refunds for violations)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Deletion of all your content and data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Notification to law enforcement or regulatory authorities
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Legal liability for damages, losses, and costs (including attorneys' fees)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Criminal prosecution where applicable
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You are responsible for any damages resulting from your violation of this Policy.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            9. Appeals
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you believe an enforcement action was taken in error, you may appeal by contacting support@tieronecontent.com within 30 days. Include:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Your account information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                The enforcement action taken
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Explanation of why you believe the action was incorrect
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Any supporting evidence
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We will review appeals in good faith but our decisions are final. Appeals do not guarantee reinstatement.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            10. No Obligation to Monitor
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            While we reserve the right to monitor the Service for compliance, we are not obligated to do so. We are not responsible for user-generated content and do not endorse or guarantee the accuracy, reliability, or legality of content shared by users.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You are solely responsible for your content and conduct on the Service.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            11. Cooperation with Authorities
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We will cooperate fully with law enforcement, regulatory authorities, and legal process. This may include:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Disclosing information about your account and activities
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Preserving data pursuant to legal hold requests
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Providing access to content and communications
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Testifying or providing evidence in legal proceedings
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            12. Updates to This Policy
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may update this Policy from time to time to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Reflect changes in the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Address new types of abuse or misuse
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Comply with legal or regulatory requirements
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Improve clarity or readability
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If we make material changes, we will:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Update the "Last Updated" date
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Provide notice through the Service or via email
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Your continued use of the Service after updates constitutes acceptance of the revised Policy.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            13. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Questions or concerns about this Acceptable Use Policy?
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 2 }}>
            Jakatech LLC<br />
            d/b/a Tier One Content<br />
            10228 E Northwest Hwy #1001<br />
            Dallas, TX 75238<br />
            Email: support@tieronecontent.com<br />
            Abuse reports: abuse@tieronecontent.com
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Typography variant="body2" color="text.secondary">
            By using Tier One Content, you acknowledge that you have read, understood, and agree to comply with this Acceptable Use Policy. Violations may result in immediate account suspension or termination without refund. Last updated: {lastUpdated}.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default AcceptableUse;
